import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PlanLayout from "./components/PlanLayout";
import Loader from "../../Component/Loader/Loader";
import { getRequest, postRequest } from "../../utils/api";

// --- Types ---
interface Plan {
    _id: string;
    ppm_plan_title: string;
}

interface RestrictionValue {
    value: number;
    on: boolean;
}

interface FieldDef {
    key: string;
    label: string;
    hint?: string;
    unlimited?: boolean;
    defaultVal: number;
    defaultOn: boolean;
}

// Define the available restriction fields and their absolute base defaults
const FIELDS: FieldDef[] = [
    { key: 'omniChannels', label: 'Omni Channels', hint: 'Count of channels — 1 = WhatsApp only · 2 = +Instagram · 3 = +Messenger', defaultVal: 1, defaultOn: true },
    { key: 'contacts', label: 'Contacts', hint: '0 = unlimited', unlimited: true, defaultVal: 5000, defaultOn: true },
    { key: 'tags', label: 'Tags', unlimited: true, defaultVal: 20, defaultOn: true },
    { key: 'campaigns', label: 'Campaigns', hint: '0 = unlimited', unlimited: true, defaultVal: 10, defaultOn: true },
    { key: 'segments', label: 'Segments', defaultVal: 5, defaultOn: true },
    { key: 'templateBots', label: 'Template Bots', defaultVal: 2, defaultOn: true },
    { key: 'messageBots', label: 'Message Bots', defaultVal: 2, defaultOn: true },
    { key: 'aiPrompts', label: 'AI Prompts', hint: '0 = unlimited', unlimited: true, defaultVal: 50, defaultOn: true },
    { key: 'agents', label: 'Agent', hint: 'default: 1', unlimited: true, defaultVal: 1, defaultOn: true },
    { key: 'conversations', label: 'Conversations', hint: '0 = unlimited', unlimited: true, defaultVal: 0, defaultOn: false },
    { key: 'teams', label: 'Teams', unlimited: true, defaultVal: 1, defaultOn: true },
    { key: 'botFlow', label: 'Bot Flow', unlimited: true, defaultVal: 2, defaultOn: true },
    { key: 'customFields', label: 'Custom Fields', unlimited: true, defaultVal: 10, defaultOn: true },
    { key: 'whatsappForm', label: 'WhatsApp Form', unlimited: true, defaultVal: 2, defaultOn: true },
    { key: 'aiCallingAgent', label: 'AI calling agent', defaultVal: 0, defaultOn: false },
];

const UsageRestrictions = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState<boolean>(true);
    const [plans, setPlans] = useState<Plan[]>([]);

    // Restrictions State: { [planId]: { [fieldKey]: { value: 10, on: true } } }
    const [restrictions, setRestrictions] = useState<Record<string, Record<string, RestrictionValue>>>({});
    // plan the bulk actions (enable / disable all, reset) apply to
    const [bulkPlanId, setBulkPlanId] = useState<string>('');

    useEffect(() => {
        fetchRestrictionsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRestrictionsData = async () => {
        setLoading(true);
        try {
            const response: any = await getRequest(`/plans/restrictions/get`);
            if (response.status === "success") {
                const fetchedPlans: Plan[] = response.data.plans;
                setPlans(fetchedPlans);

                const fetchedRestrictions = response.data.restrictions || {};
                const initialRestrictions: Record<string, Record<string, RestrictionValue>> = {};

                // Ensure every plan has a full set of fields initialized
                fetchedPlans.forEach((p) => {
                    initialRestrictions[p._id] = {};
                    FIELDS.forEach(f => {
                        if (fetchedRestrictions[p._id] && fetchedRestrictions[p._id][f.key]) {
                            initialRestrictions[p._id][f.key] = fetchedRestrictions[p._id][f.key];
                        } else {
                            // Seed defaults if missing
                            initialRestrictions[p._id][f.key] = { value: f.defaultVal, on: f.defaultOn };
                        }
                    });
                });
                setRestrictions(initialRestrictions);
                // ?plan=<title> preselects that plan, otherwise the first one
                const planTitleQuery = new URLSearchParams(location.search).get('plan')?.toLowerCase();
                const matched = fetchedPlans.find(p => p.ppm_plan_title.toLowerCase() === planTitleQuery);
                if (fetchedPlans.length > 0) setBulkPlanId((matched || fetchedPlans[0])._id);
            } else if (response.status === 'session_expired') {
                navigate('/login');
            }
        } catch (error) {
            toast.error("Failed to load usage restrictions.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const response: any = await postRequest(`/plans/restrictions/update`, { restrictions });
            if (response.status === "success") {
                toast.success(`Restrictions saved successfully`);
            } else {
                toast.error(response.message || "Failed to save restrictions.");
            }
        } catch (error) {
            toast.error("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPlan = () => {
        if (!bulkPlanId) return;
        setRestrictions(prev => {
            const newState = JSON.parse(JSON.stringify(prev));
            FIELDS.forEach(f => {
                newState[bulkPlanId][f.key] = { value: f.defaultVal, on: f.defaultOn };
            });
            return newState;
        });
        toast.info("Plan reset to defaults");
    };

    const handleBulkToggle = (turnOn: boolean) => {
        if (!bulkPlanId) return;
        setRestrictions(prev => {
            const newState = JSON.parse(JSON.stringify(prev));
            FIELDS.forEach(f => {
                newState[bulkPlanId][f.key].on = turnOn;
            });
            return newState;
        });
    };

    const patchCell = (planId: string, fieldKey: string, patch: Partial<RestrictionValue>) =>
        setRestrictions(prev => ({
            ...prev,
            [planId]: { ...prev[planId], [fieldKey]: { ...prev[planId][fieldKey], ...patch } }
        }));

    const enabledCount = plans.reduce((a, p) => a + FIELDS.filter(f => restrictions[p._id]?.[f.key]?.on).length, 0);

    return (
        <>
            {loading && <Loader />}
            <PlanLayout actions={
                <button type="button" className="cx-btn cx-btn-primary" onClick={handleSave}><i className="ri-check-line"></i>Save restrictions</button>
            }>
                <div className="cx-card">
                    <div className="cx-card-h">
                        <h6>Plan limits</h6>
                        <span className="sub">Switch off = feature blocked · empty / 0 = unlimited where noted</span>
                    </div>
                    <div className="cx-toolbar">
                        <span className="fs12 muted">Bulk edit</span>
                        <select className="cx-select" value={bulkPlanId} onChange={(e) => setBulkPlanId(e.target.value)}>
                            {plans.map(p => <option key={p._id} value={p._id}>{p.ppm_plan_title}</option>)}
                        </select>
                        <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => handleBulkToggle(true)}><i className="ri-toggle-fill"></i>Enable all</button>
                        <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={() => handleBulkToggle(false)}><i className="ri-toggle-line"></i>Disable all</button>
                        <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={handleResetPlan}><i className="ri-arrow-go-back-line"></i>Reset plan</button>
                    </div>
                    <div className="cx-tbl-wrap">
                        <table className="cx-tbl cx-lim-tbl">
                            <thead>
                                <tr>
                                    <th className="stk">Limit</th>
                                    {plans.map(p => <th key={p._id}>{p.ppm_plan_title}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {FIELDS.map(f => (
                                    <tr key={f.key}>
                                        <td className="stk">
                                            <b style={{ fontWeight: 600 }}>{f.label}</b>
                                            {f.hint && <div className="fs12 muted">{f.hint}</div>}
                                        </td>
                                        {plans.map(p => {
                                            const cell = restrictions[p._id]?.[f.key] || { value: 0, on: false };
                                            return (
                                                <td key={p._id}>
                                                    <span className="cx-lim-cell" title={cell.on ? '' : 'This feature is disabled for this plan'}>
                                                        <label className="cx-switch sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={cell.on}
                                                                onChange={(e) => patchCell(p._id, f.key, { on: e.target.checked })}
                                                                aria-label={`${f.label} · ${p.ppm_plan_title}`}
                                                            />
                                                            <span></span>
                                                        </label>
                                                        <input
                                                            className="cx-rate-in"
                                                            type="number"
                                                            min="0"
                                                            value={cell.value === 0 ? '' : cell.value}
                                                            placeholder={cell.on ? (f.unlimited ? '∞' : '0') : 'off'}
                                                            disabled={!cell.on}
                                                            onChange={(e) => patchCell(p._id, f.key, { value: Math.max(0, parseInt(e.target.value) || 0) })}
                                                        />
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="cx-tfoot">
                        <span>Limits are enforced by the backend on the client app.</span>
                        <span className="mono">{enabledCount} of {FIELDS.length * plans.length} limits enabled</span>
                    </div>
                </div>
            </PlanLayout>
        </>
    );
};

export default UsageRestrictions;
